import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import { EmptyState } from '../../../components/EmptyState';
import { ErrorNotice } from '../../../components/ErrorNotice';
import { Icon } from '../../../components/Icon';
import type { IconName } from '../../../components/Icon';
import { RichText } from '../../../components/RichText';
import { Segmented } from '../../../components/Segmented';
import { LoadingIndicator } from '../../../components/Spinner';
import { Timeline, TimelineEntry, type TimelineKind } from '../../../components/Timeline';
import { useTimelineOpen } from '../../../components/useTimelineOpen';
import { formatDateTime } from '../../../i18n';
import { type Article, downloadAttachment } from './api';
import { formatSize, useFileDownload } from './attachmentFile';
import { ArticleComposer } from './ArticleComposer';
import { useArticles, useDeleteArticle, useDeleteAttachment, usePublishArticle } from './queries';

interface ArticleTimelineProps {
  ticketId: string;
  canWrite: boolean;
  /**
   * Der Sachverhalt des Tickets, wie er darueber steht.
   *
   * Bei einem Vorgang aus dem Postfach ist der erste Beitrag dieselbe Mail,
   * die auch den Sachverhalt gefuellt hat. Er bleibt zugeklappt -- siehe
   * `openByDefault`.
   */
  descriptionHtml?: string | null;
  /**
   * Seit wann jemand von aussen auf eine Antwort wartet -- F1-50.
   *
   * Setzt die Trennlinie im Verlauf und hebt hervor, was seither
   * hereinkam. `null` heisst: Wir sind nicht am Zug, und beides entfaellt.
   */
  awaitingSince?: string | null;
}

/**
 * Der Verlauf eines Tickets -- der neueste Beitrag zuerst.
 *
 * **Die Reihenfolge ist umgekehrt chronologisch.** Der Server liefert
 * aufsteigend, weil der Ticketexport fuer eine Auskunft nach Art. 15 DSGVO
 * chronologisch gelesen werden muss. In der Bearbeiteransicht ist die Frage
 * aber eine andere: "Was ist zuletzt passiert?" Bei einem lang laufenden
 * Ticket stand die Antwort darauf bisher ganz unten, hinter dem gesamten
 * bisherigen Verlauf. Gedreht wird deshalb hier, in der Anzeige -- die
 * Reihenfolge im Export bleibt davon unberuehrt.
 *
 * **Jeder Beitrag klappt auf, der neueste steht offen.** Vierzig
 * ausgeschriebene Beitraege untereinander sind keine Uebersicht. Zugeklappt
 * zeigt eine Zeile, was die Entscheidung "oeffnen oder nicht" braucht.
 *
 * **Die Art traegt Farbe, Sinnbild und Wort.** Die haeufigste Verwechslung im
 * Ticketbetrieb ist die interne Notiz, die beim Kunden landet -- und
 * umgekehrt die Antwort, die niemand liest. Drei Traeger fuer dieselbe
 * Aussage sind dagegen nicht zu viel: Farbe allein verliert, wer sie nicht
 * unterscheiden kann.
 *
 * **Der Beitragstext wird als formatierter Text gezeigt, nicht als HTML
 * eingesetzt.** `RichText` parst das bereinigte HTML und baut daraus
 * React-Elemente; `dangerouslySetInnerHTML` kommt nicht vor. Vorher stand
 * hier der Klartext -- sicher, aber damit war jede Auszeichnung unsichtbar,
 * und ein Editor, dessen Ergebnis niemand zu sehen bekommt, ist keiner.
 *
 * **Ein Anhang laesst sich entfernen.** Nicht aus Ordnungsliebe: Wer eine
 * Datei mit personenbezogenen Daten an das falsche Ticket haengt, muss sie
 * wieder herunternehmen koennen, und zwar sofort.
 */

/** Wonach sich der Verlauf einschraenken laesst. */
type Scope = 'all' | 'customer' | 'internal';

export function ArticleTimeline({
  ticketId,
  canWrite,
  descriptionHtml,
  awaitingSince,
}: ArticleTimelineProps) {
  const { t, i18n } = useTranslation();
  const query = useArticles(ticketId);
  const publish = usePublishArticle(ticketId);
  const remove = useDeleteArticle(ticketId);
  const removeAttachment = useDeleteAttachment(ticketId);
  const fetchFile = useFileDownload();
  const [scope, setScope] = useState<Scope>('all');

  // Der Server sortiert nach (created_at, id) aufsteigend. Gedreht wird genau
  // diese Ordnung, damit zwei Beitraege aus derselben Sekunde -- etwa beim
  // Abholen mehrerer E-Mails -- nicht willkuerlich die Plaetze tauschen.
  const articles = useMemo(() => [...(query.data ?? [])].reverse(), [query.data]);
  const shown = useMemo(
    () => articles.filter((article) => matches(article, scope)),
    [articles, scope],
  );

  const ids = useMemo(() => shown.map((article) => article.id), [shown]);
  // Offen ist, was zuletzt geschah -- mit zwei Ausnahmen.
  //
  // **Kein Systemvermerk.** Ein "Prioritaet geaendert" ganz oben liesse den
  // Verlauf geschlossen aussehen.
  //
  // **Nicht der Beitrag, der den Sachverhalt gefuellt hat.** Bei einem
  // Vorgang aus dem Postfach ist der erste Beitrag dieselbe Mail, die
  // darueber schon als Sachverhalt steht. Ihn aufzuklappen hiesse, denselben
  // Text zweimal untereinander zu zeigen. Er bleibt als Nachweis stehen --
  // mit Absender, Empfaengern und Anhaengen -- nur eben zugeklappt.
  const openByDefault = useMemo(() => {
    const first = shown.find(
      (article) =>
        article.article_type !== 'system' &&
        !(descriptionHtml && article.body_html === descriptionHtml),
    );
    return first ? [first.id] : [];
  }, [shown, descriptionHtml]);
  const { isOpen, toggle, toggleAll, everythingOpen } = useTimelineOpen(ids, openByDefault);

  if (query.isPending) return <LoadingIndicator />;
  if (query.isError) return <ErrorNotice error={query.error} />;

  return (
    <div className="stack">
      {canWrite ? <ArticleComposer ticketId={ticketId} /> : null}

      {publish.isError ? <ErrorNotice error={publish.error} /> : null}
      {remove.isError ? <ErrorNotice error={remove.error} /> : null}
      {fetchFile.isError ? <ErrorNotice error={fetchFile.error} /> : null}

      {articles.length === 0 ? (
        <EmptyState icon="inbox" title={t('articles.empty')} hint={t('articles.emptyHint')} />
      ) : (
        <>
          <div className="timeline-bar">
            <Segmented
              ariaLabel={t('articles.filter')}
              value={scope}
              onChange={setScope}
              options={[
                { value: 'all', label: t('articles.filterAll') },
                { value: 'customer', label: t('articles.filterCustomer') },
                { value: 'internal', label: t('articles.filterInternal') },
              ]}
            />
            <span className="shell__spacer" />
            <Button variant="ghost" onClick={toggleAll}>
              {everythingOpen ? t('articles.collapseAll') : t('articles.expandAll')}
            </Button>
          </div>

          {shown.length === 0 ? (
            <EmptyState
              icon="filter"
              title={t('articles.filterEmpty')}
              hint={t('articles.filterEmptyHint')}
            />
          ) : null}

          <Timeline>
            {shown.map((article, index) => {
              const system = article.article_type === 'system';

              // F1-50: Die Linie steht vor dem ersten Beitrag, der **vor**
              // dem Wartebeginn liegt -- also genau dort, wo unsere letzte
              // Antwort endet. Der Verlauf laeuft rueckwaerts, deshalb
              // "der erste, der aelter ist".
              const beforeWaiting =
                awaitingSince !== null &&
                awaitingSince !== undefined &&
                article.created_at < awaitingSince;
              const previousWaiting =
                index > 0 &&
                awaitingSince !== null &&
                awaitingSince !== undefined &&
                (shown[index - 1]?.created_at ?? '') >= awaitingSince;

              return (
                <Fragment key={article.id}>
                  {beforeWaiting && previousWaiting ? (
                    <li className="timeline-since">
                      <span>
                        {t('tickets.lastAgentReply', {
                          when: formatDateTime(article.created_at, i18n.language),
                        })}
                      </span>
                    </li>
                  ) : null}
                  <TimelineEntry
                    // F1-50: Was seit unserer letzten Antwort hereinkam,
                    // traegt einen zusaetzlichen Rahmen.
                    highlighted={!beforeWaiting && awaitingSince !== null && !system}
                    kind={kindOf(article)}
                    icon={iconOf(article)}
                    toggleLabel={t('articles.toggleEntry')}
                    open={isOpen(article.id)}
                    onToggle={() => {
                      toggle(article.id);
                    }}
                    // Ein Systemvermerk sagt in seiner Zeile bereits alles. Ein
                    // Pfeil daran waere ein Versprechen auf mehr.
                    collapsible={!system || article.body_text.length > 80}
                    badges={
                      <>
                        <Badge tone={toneOf(article)}>
                          {t(`articleType.${article.article_type}`)}
                        </Badge>
                        {article.visibility === 'internal' && !system ? (
                          <Badge tone="warning">{t('articles.internal')}</Badge>
                        ) : null}
                        {article.is_draft ? (
                          <Badge tone="neutral">{t('articles.draft')}</Badge>
                        ) : null}
                      </>
                    }
                    who={system ? summarise(article) : whoOf(article)}
                    preview={system ? undefined : summarise(article)}
                    time={formatDateTime(article.created_at, i18n.language)}
                    foot={
                      <>
                        {(article.attachments ?? []).map((attachment) => (
                          <span key={attachment.id} className="timeline__attachment">
                            <Button
                              variant="ghost"
                              onClick={() => {
                                fetchFile.mutate({
                                  fetchFile: () => downloadAttachment(attachment.id),
                                  fallbackName: attachment.filename,
                                });
                              }}
                            >
                              <Icon name="paperclip" />
                              {attachment.filename}
                            </Button>
                            <span className="table__muted">
                              {formatSize(attachment.size_bytes)}
                            </span>
                            {/* Ungeprueft heisst ungeprueft -- docs/04,
                              Abschnitt 7. Die Virenpruefung ist
                              zurueckgestellt; solange sie fehlt, muss das am
                              Anhang stehen. Ein Anhang ohne Hinweis sieht aus
                              wie ein geprueftes. */}
                            {attachment.virus_scanned_at === null ? (
                              <Badge tone="warning">{t('articles.notScanned')}</Badge>
                            ) : null}
                            {canWrite ? (
                              <Button
                                variant="ghost"
                                busy={removeAttachment.isPending}
                                onClick={() => {
                                  if (!window.confirm(t('articles.removeAttachmentConfirm')))
                                    return;
                                  removeAttachment.mutate(attachment.id);
                                }}
                              >
                                {t('articles.removeAttachment')}
                              </Button>
                            ) : null}
                          </span>
                        ))}

                        {article.is_draft && canWrite ? (
                          <>
                            <span className="shell__spacer" />
                            <Button
                              variant="secondary"
                              busy={publish.isPending && publish.variables === article.id}
                              onClick={() => {
                                publish.mutate(article.id);
                              }}
                            >
                              {t('articles.publish')}
                            </Button>
                            <Button
                              variant="ghost"
                              busy={remove.isPending && remove.variables === article.id}
                              onClick={() => {
                                remove.mutate(article.id);
                              }}
                            >
                              {t('articles.discardDraft')}
                            </Button>
                          </>
                        ) : null}
                      </>
                    }
                  >
                    {article.subject ? (
                      <p className="timeline__subject">{article.subject}</p>
                    ) : null}

                    {/* Wer die Mail bekommen hat -- F3-22. Nur hier, im
                        Verlauf der Bearbeiter; das Portal zeigt keine
                        Empfaenger, und die Blindkopie stand auch im Kopf
                        der Mail nicht. */}
                    <RecipientLine article={article} />

                    <RichText
                      html={article.body_html}
                      fallback={
                        <p className="table__muted">{article.body_text || t('app.none')}</p>
                      }
                    />
                  </TimelineEntry>
                </Fragment>
              );
            })}
          </Timeline>
        </>
      )}
    </div>
  );
}

/**
 * Passt der Beitrag zur gewaehlten Einschraenkung?
 *
 * "Nur Kundenkontakt" heisst: was der Kunde geschrieben hat oder lesen kann.
 * Systemvermerke fallen dabei heraus -- sie sind weder das eine noch das
 * andere, und wer den Schriftwechsel nachlesen will, will sie nicht dazwischen.
 */
function matches(article: Article, scope: Scope): boolean {
  if (scope === 'all') return true;
  if (article.article_type === 'system') return false;
  return scope === 'internal' ? article.visibility === 'internal' : article.visibility === 'public';
}

function kindOf(article: Article): TimelineKind {
  switch (article.article_type) {
    case 'system':
      return 'system';
    case 'incoming_email':
    case 'portal':
      return 'in';
    case 'outgoing_email':
      return 'out';
    case 'phone':
      return 'phone';
    default:
      return article.visibility === 'internal' ? 'internal' : 'out';
  }
}

function iconOf(article: Article): IconName {
  switch (article.article_type) {
    case 'system':
      return 'system';
    case 'incoming_email':
      return 'mailIn';
    case 'portal':
      return 'crm';
    case 'outgoing_email':
      return 'mailOut';
    case 'phone':
      return 'phone';
    default:
      return article.visibility === 'internal' ? 'lock' : 'note';
  }
}

function toneOf(article: Article) {
  switch (article.article_type) {
    case 'incoming_email':
    case 'portal':
      return 'info' as const;
    case 'outgoing_email':
      return 'success' as const;
    default:
      return 'neutral' as const;
  }
}

/**
 * Wer geschrieben hat.
 *
 * Der Name des Bearbeiters, wenn der Server ihn kennt; sonst die
 * Absenderadresse. Bei einer eingegangenen Mail ist die Adresse die
 * genauere Angabe -- einen Kontaktnamen daraus zu raten waere eine
 * Zuschreibung, die niemand geprueft hat.
 */
function whoOf(article: Article): string {
  return article.author_name ?? article.from_address ?? '';
}

/** Die erste Zeile des Beitrags, fuer die zugeklappte Ansicht. */
function summarise(article: Article): string {
  const text = article.body_text.replace(/\s+/g, ' ').trim();
  return text.length > 160 ? `${text.slice(0, 160)}…` : text;
}

/** "An · CC · BCC" eines Mailbeitrags -- leer bei allem anderen. */
function RecipientLine({ article }: { article: Article }) {
  const { t } = useTranslation();
  const teile = (
    [
      ['to', article.to_addresses],
      ['cc', article.cc_addresses],
      ['bcc', article.bcc_addresses],
    ] as const
  )
    // Beitraege aus einem aelteren Stand des Zwischenspeichers tragen die
    // Felder womoeglich nicht -- dann gibt es eben keine Zeile.
    .map(([field, addresses]) => [field, (addresses as string[] | undefined) ?? []] as const)
    .filter(([, addresses]) => addresses.length > 0);
  if (teile.length === 0) return null;
  return (
    <p className="timeline__recipients">
      {teile.map(([field, addresses]) => (
        <span key={field}>
          <span className="timeline__recipients-label">{t(`articles.${field}`)}:</span>{' '}
          {addresses.join(', ')}
        </span>
      ))}
    </p>
  );
}
